/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { FC, useMemo } from 'react'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'

import { createStudyNote, createStudyNoteVariables } from '__generatedTypes__/createStudyNote'
import { findAllStudyNote_findAllStudyNote_items as IStudyNotes } from '__generatedTypes__/findAllStudyNote'
import { findAllStudyNoteGroup_findAllStudyNoteGroup_items as IGroup } from '__generatedTypes__/findAllStudyNoteGroup'
import { removeStudyNote, removeStudyNoteVariables } from '__generatedTypes__/removeStudyNote'
import { updateStudyNote, updateStudyNoteVariables } from '__generatedTypes__/updateStudyNote'
import client from 'configs/apolloClient'
import { CREATE_STUDY_NOTE, DELETE_STUDY_NOTE, FIND_ALL_STUDY_NOTES, UPDATE_STUDY_NOTE } from 'queries/studyNote'
import { FieldValues } from 'utils/types'
import { toast } from 'utils/toast'
import { MembershipType } from '__generatedTypes__/globalTypes'
import {
  BOOSTER_ENV_TYPE,
  GOOGLE_DRIVE_TYPE,
  UpdateCardData,
  URLCardForm,
  getNoteEnvironmentItems,
} from 'components/URLCardForm'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import * as classes from 'components/shared/GroupCards/commonStyles'
import { getNoteEnvironmentConfig } from '../platformConfig'

interface IProps {
  studyNote: IStudyNotes | { id: string; studyNoteGroup: { id: string }; membershipLevel?: MembershipType }
  onClose: () => void
  studyNoteGroups: IGroup[]
  studyNotesLength: number
  isEditMode: boolean
}

const ManageStudyNoteModal: FC<IProps> = ({ studyNote, onClose, studyNoteGroups, studyNotesLength, isEditMode }) => {
  const { selectedPlatformSlug } = usePlatformsAndSubjects({})
  const noteEnvironmentItems = getNoteEnvironmentItems(getNoteEnvironmentConfig(selectedPlatformSlug))
  const [updateStudyNote, { loading: updateLoading }] = useMutation<updateStudyNote, updateStudyNoteVariables>(
    UPDATE_STUDY_NOTE
  )
  const [removeStudyNote] = useMutation<removeStudyNote, removeStudyNoteVariables>(DELETE_STUDY_NOTE)

  const handleCloseAndUpdate = () => {
    client
      .refetchQueries({
        include: [FIND_ALL_STUDY_NOTES],
      })
      .then(onClose)
  }

  const [createStudyNote, { loading: createLoading }] = useMutation<createStudyNote, createStudyNoteVariables>(
    CREATE_STUDY_NOTE
  )

  const handleGroupRemove = async () => {
    if (!studyNote?.id) return
    try {
      await removeStudyNote({ variables: { removeStudyNoteId: studyNote.id } })
      handleCloseAndUpdate()

      toast.success('Card deleted successfully')
    } catch (error) {
      toast.error('Failed to delete card')
    }
  }

  const handleUpdateCard = async (data: Partial<UpdateCardData>) => {
    await updateStudyNote({
      variables: {
        params: {
          id: data.id,
          name: data.name,
          description: data.description,
          link: data.link,
          membershipLevel: data.membershipLevel,
          ...(data.group?.id && { studyNoteGroup: { id: data.group.id } }),
          isBoosterEnvironment: data.isBoosterEnvironmentUpd,
          isDocumentUpdated: data.isDocumentUpdated,
        },
      },
      refetchQueries: () => [
        {
          query: FIND_ALL_STUDY_NOTES,
          variables: {
            query: {
              take: TAKE_ALL_ITEMS_COUNT,
              where: {
                studyNoteGroup: {
                  id: data.group?.id,
                },
              },
            },
          },
        },
      ],
    })
    handleCloseAndUpdate()
  }

  const handleCreateCard = async (data: FieldValues) => {
    await createStudyNote({
      variables: {
        params: {
          name: data.name,
          description: data.description,
          order: studyNotesLength,
          link: data.link,
          membershipLevel: data.membershipLevel,
          studyNoteGroup: {
            id: data.group?.id,
          },
          isBoosterEnvironment: data.noteEnvironment?.id === BOOSTER_ENV_TYPE.id,
        },
      },
    })
    handleCloseAndUpdate()
  }

  const initialValues = useMemo(
    () => ({
      ...(isEditMode
        ? {
            ...studyNote,
            group: {
              id: studyNote.studyNoteGroup?.id,
            },
            membershipLevel: studyNote.membershipLevel,
          }
        : studyNote),
      noteEnvironment: (studyNote as IStudyNotes).isBoosterEnvironment ? BOOSTER_ENV_TYPE : GOOGLE_DRIVE_TYPE,
    }),
    [studyNote]
  )

  return (
    <div css={classes.modalContainer}>
      <URLCardForm<IStudyNotes>
        title={isEditMode ? 'Edit Card' : 'Create Card'}
        onGroupRemove={handleGroupRemove}
        groups={studyNoteGroups}
        onClose={onClose}
        isEditMode={isEditMode}
        card={studyNote as IStudyNotes}
        updateCard={handleUpdateCard}
        createCard={handleCreateCard}
        isLoading={updateLoading || createLoading}
        initialValues={initialValues}
        noteEnvironmentItems={noteEnvironmentItems}
      />
    </div>
  )
}

export default ManageStudyNoteModal
